const STORAGE_KEY = 'quizter:playerProgress';

const buildDateKey = (date = new Date()) => date.toISOString().slice(0, 10);

const getYesterdayKey = () => {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  return buildDateKey(date);
};

const normalizeProgress = (progress = {}) => {
  const todayKey = buildDateKey();
  const dailyDate = progress.dailyDate || todayKey;
  const dailyProgress = dailyDate === todayKey ? Number(progress.dailyProgress || 0) : 0;

  return {
    streak: Number(progress.streak || 0),
    lastPlayedDate: progress.lastPlayedDate || null,
    dailyGoal: Number(progress.dailyGoal || 5),
    dailyProgress,
    dailyDate: todayKey,
    completedRuns: Array.isArray(progress.completedRuns) ? progress.completedRuns : [],
    recommendedDifficulty: progress.recommendedDifficulty || null,
    recommendationReason: progress.recommendationReason || null,
    lastAccuracy: Number.isFinite(progress.lastAccuracy) ? progress.lastAccuracy : null,
    lastDifficulty: progress.lastDifficulty || null,
    updatedAt: progress.updatedAt || null
  };
};

const clampDifficulty = (value) => {
  const valid = ['children', 'youth', 'adults', 'family'];
  if (!value) return null;
  const lowered = String(value).toLowerCase();
  return valid.includes(lowered) ? lowered : null;
};

const deriveRecommendation = (difficulty, accuracy) => {
  const baseDifficulty = clampDifficulty(difficulty);
  const order = ['children', 'youth', 'adults'];
  let recommended = baseDifficulty;
  let reason = 'Behåller nuvarande nivå.';

  if (baseDifficulty && order.includes(baseDifficulty) && Number.isFinite(accuracy)) {
    const index = order.indexOf(baseDifficulty);
    if (accuracy >= 0.8 && index < order.length - 1) {
      recommended = order[index + 1];
      reason = 'Hög träffsäkerhet – vi höjer nivån lite.';
    } else if (accuracy <= 0.4 && index > 0) {
      recommended = order[index - 1];
      reason = 'Många fel – vi sänker nivån för att göra det roligare.';
    } else {
      reason = 'Lagom nivå – vi behåller svårigheten.';
    }
  } else if (baseDifficulty === 'family') {
    recommended = baseDifficulty;
    reason = 'Familjeläge behålls.';
  }

  return { recommended, reason };
};

class PlayerProgressService {
  getProgress() {
    if (typeof window === 'undefined') return normalizeProgress();
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      return normalizeProgress(stored ? JSON.parse(stored) : {});
    } catch (error) {
      console.warn('[PlayerProgress] Kunde inte läsa progress:', error);
      return normalizeProgress();
    }
  }

  saveProgress(progress) {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
      window.dispatchEvent(new Event('playerProgress:changed'));
    } catch (error) {
      console.warn('[PlayerProgress] Kunde inte spara progress:', error);
    }
  }

  recordRunCompletion({ runId, correctCount, totalCount, difficulty }) {
    if (!runId) return this.getProgress();
    const progress = this.getProgress();
    if (progress.completedRuns.includes(runId)) {
      return progress;
    }

    const todayKey = buildDateKey();
    const yesterdayKey = getYesterdayKey();
    let streak = progress.streak || 0;
    if (progress.lastPlayedDate === todayKey) {
      streak = streak || 1;
    } else if (progress.lastPlayedDate === yesterdayKey) {
      streak = streak + 1;
    } else {
      streak = 1;
    }

    const updatedDailyProgress = (progress.dailyDate === todayKey ? progress.dailyProgress : 0)
      + (Number(totalCount) || 0);

    const accuracy = Number(totalCount) > 0 ? Number(correctCount) / Number(totalCount) : null;
    const { recommended, reason } = deriveRecommendation(difficulty, accuracy);

    const completedRuns = [runId, ...progress.completedRuns].slice(0, 20);
    const nextProgress = {
      ...progress,
      streak,
      lastPlayedDate: todayKey,
      dailyGoal: progress.dailyGoal || 5,
      dailyProgress: updatedDailyProgress,
      dailyDate: todayKey,
      completedRuns,
      recommendedDifficulty: recommended || progress.recommendedDifficulty,
      recommendationReason: reason,
      lastAccuracy: accuracy,
      lastDifficulty: difficulty || progress.lastDifficulty,
      updatedAt: Date.now()
    };

    this.saveProgress(nextProgress);
    return nextProgress;
  }

  getRecommendedDifficulty() {
    const progress = this.getProgress();
    return progress.recommendedDifficulty;
  }
}

export const playerProgressService = new PlayerProgressService();
